import {
    decimalToLocalizedString,
    formatNumberWithAbbreviation,
    getFormattedValueAndUnitFromNumber,
} from 'ComponentLibrary/src/common/numbers'

export class AreaValue {
    readonly value: number
    readonly unit: 'sqm' | 'living_space'

    constructor(value: number, unit: 'sqm' | 'living_space' = 'sqm') {
        this.value = value
        this.unit = unit
    }

    unitSymbol(locale = 'de-DE') {
        switch (this.unit) {
            case 'living_space':
                return locale === 'en-US' ? 'm² living area' : 'm² Wfl.'
            case 'sqm':
            default:
                return 'm²'
        }
    }

    format({ pretty = false, withUnit = true, withoutSign = false, minimumNumberOfDigits = 0, locale = 'de-DE' } = {}): string {
        const value = withoutSign ? Math.abs(this.value) : this.value
        const displayValue = pretty
            ? formatNumberWithAbbreviation(value, 2, false, undefined, locale)
            : decimalToLocalizedString(value, false, 2, minimumNumberOfDigits, locale)
        return `${displayValue}${withUnit ? ` ${this.unitSymbol(locale)}` : ''}`
    }

    static fromDataObject(data: AreaValueDataObject): AreaValue {
        return new AreaValue(data.value, data.unit)
    }

    divide(other: AreaValue): number {
        if (this.unit !== other.unit) {
            throw new UnitMismatchException()
        }
        return this.value / other.value
    }
}

export function getFormattedValueAndUnitFromAreaValue(areaValue?: AreaValueDataObject, locale = 'de-DE') {
    if (!areaValue) {
        return { value: '-' }
    }
    const areaValueClassObject = AreaValue.fromDataObject(areaValue)
    return getFormattedValueAndUnitFromNumber(areaValueClassObject.value, areaValueClassObject.unitSymbol(locale), undefined, locale)
}

export function addAreaValueDataObjects(areaValue: AreaValueDataObject, otherValue: AreaValueDataObject): AreaValueDataObject {
    if (areaValue.unit !== otherValue.unit) {
        throw new UnitMismatchException()
    }
    return {
        value: areaValue.value + otherValue.value,
        unit: areaValue.unit,
    }
}

export function subtractAreaValueDataObjects(areaValue: AreaValueDataObject, otherValue: AreaValueDataObject): AreaValueDataObject {
    if (areaValue.unit !== otherValue.unit) {
        throw new UnitMismatchException()
    }
    return {
        value: areaValue.value - otherValue.value,
        unit: areaValue.unit,
    }
}

export type AreaValueDataObject = { value: number; unit: 'sqm' | 'living_space' }

export const isAreaValue = (value: AreaValue | unknown, allowNaNAmount = false): value is AreaValue => {
    if (!(value instanceof AreaValue)) {
        return false
    }
    return allowNaNAmount ? true : !isNaN(value?.value)
}

class UnitMismatchException extends Error {
    constructor() {
        super('Cannot add area values with mismatching units')
        this.name = 'UnitMismatchException'
    }
}
